import { API_URL } from "../config";

export const isTokenExpired = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
    } catch (e) {
        return true;
    }
};

/**
 * Checks if the current access token is valid.
 * If expired, attempts to refresh it using the refresh token.
 * Returns the valid access token if successful, or null if authentication fails.
 * Also handles user journey syncing on successful refresh and cleanup on failure.
 */
export const getValidAccessToken = async (): Promise<string | null> => {
    let token = localStorage.getItem("access_token");

    if (!token) {
        return null;
    }

    if (!isTokenExpired(token)) {
        return token;
    }

    // Token is expired, try to refresh
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
        // No refresh token available to renew the session
        handleLogoutCleanup();
        return null;
    }

    try {
        const response = await fetch(`${API_URL}/auth/token/refresh/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh: refreshToken }),
        });

        if (response.ok) {
            const data = await response.json();
            const newAccessToken = data.access;

            localStorage.setItem("access_token", newAccessToken);

            // Note: User explicitly removed refresh token update in previous step, so we stick to that.

            token = newAccessToken;

            // Sync user journey using the new token
            const userEvents = localStorage.getItem("userEvents");
            if (userEvents) {
                try {
                    await fetch(`${API_URL}/auth/user-journey`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: userEvents,
                    });
                    localStorage.removeItem("userEvents");
                } catch (e) {
                    console.error("Error sending user journey:", e);
                }
            }

            return token;
        } else {
            throw new Error("Token refresh failed");
        }
    } catch (error) {
        console.error("Auth verification error:", error);
        handleLogoutCleanup();
        return null;
    }
};

const handleLogoutCleanup = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("userProfile");
};
